import { checkNever } from '@infinex-private/ts-utils/assert';

import { ActivityType } from '../../../types';
import type { Activity } from '../../../types';

/**
 * Extracts all relevant addresses from an activity based on its type.
 * Returns an array of addresses that should be considered when filtering activities by wallet.
 *
 * @param activity - The activity to extract addresses from
 * @returns Array of addresses, or empty array if activity doesn't have a wallet-specific address
 */
export const getActivityAddresses = (activity: Activity): string[] => {
  switch (activity.type) {
    case ActivityType.SEND:
      return [activity.fromAddress, activity.toAddress].filter(Boolean);

    case ActivityType.SWIDGE:
      return [
        activity.rawResponse?.quote?.from?.address,
        activity.rawResponse?.quote?.to?.address,
      ].filter(Boolean);

    case ActivityType.EXTERNAL_APP_ACTION:
      return [activity.walletAddress];

    case ActivityType.AIRDROP:
    case ActivityType.AIRDROP_CLAIM:
      return activity.receiverAddress ? [activity.receiverAddress] : [];

    case ActivityType.CASHBACK_CLAIM:
      // For the foreseeable future, cashback claims don't have a wallet-specific address field
      // They're associated with the account, not a specific wallet
      return [];

    case ActivityType.PATRON_PURCHASE:
    case ActivityType.PATRON_REDEMPTION:
      // Patron activities were account-level (burning from vault or redeeming GP)
      // Not associated with a specific wallet address
      return [];

    case ActivityType.GAS:
      return [activity.toAddress, activity.fromAddress].filter(Boolean);

    case ActivityType.PRIZE_DRAW_WIN:
      return [];

    case ActivityType.SONAR_SALE_PURCHASE:
      // Sonar sale purchases are made from the Infinex wallet
      return activity.walletAddress ? [activity.walletAddress] : [];

    case ActivityType.INX_CLAIM:
    case ActivityType.INX_EARLY_UNLOCK:
    case ActivityType.INX_VOUCHER_REDEMPTION:
      // INX activities are associated with a wallet address
      return activity.walletAddress ? [activity.walletAddress] : [];

    case ActivityType.PRIVATE_SEND:
      // Funded via an opaque escrow deposit — not attributable to a single
      // wallet from this record (account-level, like cashback claims).
      return [];

    case ActivityType.PRIVATE_RECEIVE:
      return activity.claimDestinationType === 'wallet' &&
        activity.claimedToAddress
        ? [activity.claimedToAddress]
        : [];

    default:
      checkNever(activity);
      return [];
  }
};
